import React from 'react';

import { Box, HStack, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';

import ActionButton, { IActionButtonProps } from 'components/action-button';
import { StyledLoyaltyPointsIcon } from 'components/loyalty-points-button/styled';
import { useLoyaltyPoints } from 'hooks/loyalty/use-loyalty-points';
import {
  Container,
  Headline,
  StyledRewardsLogo,
} from 'pages/account/account-loyalty-header.styled';
import { CustomEventNames } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';
import { isNative } from 'utils/environment';
import { routes } from 'utils/routing';

const SignUpActionButton = ({ onPress }: Pick<IActionButtonProps, 'onPress'>) => {
  return (
    <ActionButton
      width={'$56'}
      height={'$12'}
      alignSelf={'center'}
      testID="account-sign-up-link"
      eventName={CustomEventNames.BUTTON_CLICK_SIGN_UP}
      onPress={onPress}
    >
      {t('signUpNow')}
    </ActionButton>
  );
};

const RedeemPointsActionButton = ({ onPress }: Pick<IActionButtonProps, 'onPress'>) => {
  const router = useRouter();
  return (
    <ActionButton
      width={'$56'}
      height={'$12'}
      alignSelf={'center'}
      testID="account-sign-up-link"
      backgroundColor={primitive.bk.$meltyYellow}
      color={primitive.bk.$bbqBrown}
      eventName={CustomEventNames.BUTTON_CLICK_REDEEM_NOW}
      onPress={() => router.replace(routes.rewards)}
    >
      {t('redeemNow')}
    </ActionButton>
  );
};

export const AccountLoyaltyHeader = () => {
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const {
    userHasExpiringPoints,
    pointsExpirationDate,
    pointsToExpire,
    isNewPointsModel,
    currentPoints,
  } = useLoyaltyPoints(loyaltyUser);

  const loyaltyCreateAccountHeadline = loyaltyEnabled
    ? t('loyaltyCreateAccountHeadline')
    : t('createAccountHeadline');

  return (
    <Container>
      {!isAuthenticated && (
        <Headline testID="account-sign-up-label" marginBottom={'$0'}>
          {loyaltyCreateAccountHeadline}
        </Headline>
      )}

      {loyaltyEnabled && (
        <StyledRewardsLogo
          testID="account-sign-up-loyalty-logo"
          maxWidth={'$80'}
          alignSelf={'center'}
        />
      )}

      {isAuthenticated && loyaltyUser && (
        <Box marginBottom={'$4'}>
          <HStack justifyContent={'center'}>
            <Text fontSize={'xl'} lineHeight={'2xl'} bold alignSelf={'center'} marginRight={'$1'}>
              {t('youHave')}
            </Text>
            <HStack
              backgroundColor={primitive.bk.$bbqBrown}
              padding={'$1.5'}
              borderRadius={'sm'}
              alignContent={'center'}
              marginRight={'$1'}
            >
              <StyledLoyaltyPointsIcon marginRight={'$1'} alignSelf={'center'} reversed={false} />
              <Text
                color={primitive.$white}
                fontSize={'xl'}
                bold
                alignContent={'center'}
                paddingTop={isNative ? '$1.5' : undefined}
              >
                {String(currentPoints)}
              </Text>
            </HStack>
            <Text fontSize={'xl'} lineHeight={'2xl'} bold alignSelf={'center'}>
              {t('available').toLowerCase()}
            </Text>
          </HStack>

          {userHasExpiringPoints && (
            <>
              {isNewPointsModel ? (
                <Text alignSelf={'center'} numberOfLines={2}>
                  {t('accountRewardsPointsExpiration', {
                    pointsToExpire,
                    expirationDateTime: pointsExpirationDate.formatted,
                  })}
                </Text>
              ) : (
                // This branch is required here because CA users are still using the old expiration model
                // once CA users are migrated to the new model, this branch can be removed
                <Text alignSelf={'center'} textAlign="center" numberOfLines={2}>
                  {t('yourPointsWillExpire')}
                  {'\n'}
                  {pointsExpirationDate.formatted}
                </Text>
              )}
            </>
          )}
        </Box>
      )}

      {!isAuthenticated && <SignUpActionButton onPress={() => openSignInModal()} />}
      {isAuthenticated && userHasExpiringPoints && <RedeemPointsActionButton />}
    </Container>
  );
};
